import {apiSettings} from './api/index';
import {format, parse, parseISO} from 'date-fns';

export const getDocumentURL = documentId => {
  const token = localStorage.getItem('userToken');

  return token
    ? `${apiSettings.baseURL}/v1_0/credit-requests/download-document/${documentId}/?X-User-Token=${token}`
    : '';
};

/**
 * trim the leading and following whitespace;
 * remove multiple whitespace between the characters
 */
export const formatWhiteSpaceFormField = value =>
  value ? value.trim().replace(/\s\s+/g, ' ') : '';

/**
 * remove whitespace;
 */
export const formatNonWhiteSpaceFormField = value =>
  value ? value.replace(/\s/g, '') : null;

/**
 * only numbers;
 */
export const parseOnlyNumericFormField = value =>
  value ? value.replace(/[^0-9]/g, '') : null;

export const yearMonthDayDateWithEnDash = (str = '') => {
  const trimmed = str.trim();
  if (RegExp('[0-9]{4}[/][0-9]{2}[/][0-9]{2}').test(trimmed)) {
    return trimmed;
  }

  if (
    RegExp('[0-9]{2}[.][0-9]{2}[.][0-9]{4}').test(trimmed) &&
    parseISO(trimmed.split('.').reverse().join('-')).toString() !==
      'Invalid Date'
  ) {
    return trimmed.split('.').reverse().join('-');
  }

  return trimmed;
};

export const mozillaFirefoxRedirect = (e, url) => {
  if (!navigator.userAgent.includes('Firefox')) {
    return;
  }

  e.preventDefault();
  window.open(url ? url : '', '_blank');
  return false;
};

export const parseDate = (value, format = 'yyyy-MM-dd') =>
  parse(value, format, new Date());

export const formatDateFormField = value => {
  if (!value) return '';

  return (
    (new RegExp(/\d{4}-\d{2}-\d{2}/).test(value) &&
      parseISO(value).toString() !== 'Invalid Date' &&
      format(parseISO(value), 'dd.MM.yyyy')) ||
    value
  );
};

export const parseDateFormField = value => {
  if (!value) return '';

  return yearMonthDayDateWithEnDash(value);
};

export const formatNumberFormField = value =>
  typeof value === 'number' && value !== '' ? String(value) : '';

export const parseNumberFormField = value =>
  typeof value === 'string' && value !== '' ? Number(value) : undefined;

export const parseNumberIntegerFormField = value => {
  return typeof value === 'string' && value !== ''
    ? Math.trunc(value)
    : undefined;
};

export const parseRadioFormField = value => JSON.parse(value);

export const getMimeType = signature => {
  switch (signature) {
    /**
      https://mimesniff.spec.whatwg.org/#matching-an-image-type-pattern
       **/
    case '89504E47':
      return 'image/png';
    case '47494638':
      return 'image/gif';
    case '49492A00':
    case '4D4D002A':
      return 'image/tiff';
    case '52494646':
      return 'image/webp';
    case 'FFD8FFE0':
    case 'FFD8FFDB':
    case 'FFD8FFE1':
      return 'image/jpeg';
    /**
      https://mimesniff.spec.whatwg.org/#identifying-a-resource-with-an-unknown-mime-type
      **/
    case '25504446':
      return 'application/pdf';
    default:
      return 'Unknown filetype';
  }
};

export const getFileExtensionFromMimeType = mime => {
  switch (mime) {
    case 'image/png':
      return '.png';
    case 'image/gif':
      return '.gif';
    case 'image/tiff':
      return '.tif, .tiff';
    case 'image/webp':
      return '.webp';
    case 'image/jpeg':
      return '.jpg, .jpeg';
    case 'application/pdf':
      return '.pdf';
    default:
      return '';
  }
};

export const arrayBufferToBase64 = buffer => {
  // https://gist.github.com/jonleighton/958841#gistcomment-2915919
  return window.btoa(
    new Uint8Array(buffer).reduce((data, byte) => {
      return data + String.fromCharCode(byte);
    }, ''),
  );
};

export const patchPayload = (type, status) => {
  switch (type) {
    case 'IDScan':
      return {is_ocr_step_done: true};
    case 'Selfie':
      return {is_selfie_step_done: true};
    case 'Liveness':
      return {is_liveness_step_done: true};
    case 'ESign':
      if (status === 'awaiting_anaf_consent') {
        return {is_awaiting_anaf_consent_step_done: true};
      }

      if (status === 'awaiting_esign') {
        return {is_awaiting_esign_step_done: true};
      }

      break;
    case 'Idenfy':
      return {user_kyc_done: true};
    default:
      return {};
  }
};

//from ArrayBuffer
export const createFileUrl = (data, type = 'application/pdf') => {
  const file = new Blob([data], {type});
  const objectURL = window.URL.createObjectURL(file);

  window.onbeforeunload = () => {
    window.URL.revokeObjectURL(objectURL);
  };

  return objectURL;
};

//from Base64
export const openBase64File = (objectURL, type) => {
  const byteCharacters = window.atob(objectURL);
  const byteNumbers = [];

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);

  return createFileUrl(byteArray, type);
};

export const determineOverSize = fileSize => {
  /**
   * the subtraction is made to convert from KB to MB
   */
  return fileSize / 1024 > 2048;
};

export const maxSizeNumber = 2;

export const handleAutoDownloadFile = res => {
  const blob = new Blob([res.data], {type: 'application/pdf'});
  const fileURL = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = fileURL;
  link.download = res.headers['x-filename'].split(',')[0];
  link.click();

  mozillaFirefoxRedirect(fileURL);
  return false;
};

export const sortListByKey = (list, key = 'name') => {
  return list?.sort((a, b) => {
    const keyA = a[key].toUpperCase();
    const keyB = b[key].toUpperCase();

    if (keyA < keyB) {
      return -1;
    }
    if (keyA > keyB) {
      return 1;
    }

    return 0;
  });
};

export const filterPostalCodes = (postalCodes, street, cb) => {
  const list = postalCodes?.filter(({name}) => {
    const streetNameNormalized = street ?? '';
    const streetNameWords = streetNameNormalized
      .split(' ')
      .filter(w => w !== '');
    let hasWord = false;

    for (const word of streetNameWords) {
      if (name.toLowerCase().includes(word.toLowerCase())) {
        hasWord = true;
        break;
      }
    }

    return hasWord;
  });

  const filteredList = list?.length ? list : postalCodes;
  cb(sortListByKey(filteredList));
};

const numToFixed = n => n.toFixed(2).replace('.', ',');
export const formatRON = n =>
  n.toLocaleString('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' RON';
export const formatPercent = n => numToFixed(n) + ' %';

export const offersByGroup = offers =>
  offers
    .sort((a, b) => (a.id > b.id ? -1 : 1))
    .sort((a, b) => (a.insurance > b.insurance ? -1 : 1))
    .sort((a, b) =>
      a.financial_product_grace_period > b.financial_product_grace_period
        ? -1
        : 1,
    )
    .reduce((acc, item) => {
      const group = acc[item.id] || acc[item.paired_offer_id];

      group ? group.push(item) : (acc[item.id] = Array(item));

      return acc;
    }, {});
